import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { LauncherButton } from './components/LauncherButton';
import { ChatPanel, type EscalationState, type HandoffState } from './components/ChatPanel';
import {
  getWidgetConfig,
  getChatHistory,
  postChat,
  postEscalate,
  postHandoff,
  getLiveAvailability,
  getLiveState,
  sendLiveMessage,
  postFeedback,
  type WidgetConfig,
  type ChatChunk
} from './lib/api';
import { getConversationId, setConversationId, clearConversationId } from './lib/storage';
import { getTurnstileToken } from './lib/turnstile';
import { playMessageChime } from './lib/sound';

type View = 'closed' | 'open';

// Live-chat phase. Shared with ChatPanel via this exported union.
export type LivePhase = 'off' | 'connecting' | 'active' | 'ended';

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  /**
   * Server id of the persisted assistant turn, from the stream's meta frame.
   * Present only on bot turns from this session, which is exactly where the
   * thumbs are offered: rehydrated history has no ids, and live-agent turns
   * are not rated.
   */
  id?: string;
}

export default function App({ apiBase }: { apiBase: string }) {
  const [view, setView] = useState<View>('closed');
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [busy, setBusy] = useState(false);
  const [terminal, setTerminal] = useState(false);
  const [escalation, setEscalation] = useState<EscalationState>({ kind: 'hidden' });
  const [handoff, setHandoff] = useState<HandoffState>({ kind: 'hidden' });
  const [liveAvailable, setLiveAvailable] = useState(false);
  const [livePhase, setLivePhase] = useState<LivePhase>('off');
  const [liveAgentName, setLiveAgentName] = useState<string | null>(null);
  const [liveEndedReason, setLiveEndedReason] = useState<string | null>(null);
  const [liveTicketCode, setLiveTicketCode] = useState<string | null>(null);
  const [liveCursor, setLiveCursor] = useState<string | undefined>(undefined);
  // Unread agent messages while the panel is closed (badge on the launcher).
  const [unread, setUnread] = useState(0);
  // `view` read inside the live-poll closure, which doesn't re-run on view change.
  const viewRef = useRef<View>(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  // Contents of visitor messages shown optimistically but not yet confirmed by
  // the poll. The poll skips the matching role:'user' echo so it isn't
  // double-rendered. FIFO — visitor messages send and echo back in order.
  const pendingEchoRef = useRef<string[]>([]);
  const [hiddenTurnstileContainer] = useState(() => {
    const d = document.createElement('div');
    d.style.position = 'fixed';
    d.style.left = '-10000px';
    return d;
  });

  useEffect(() => {
    getWidgetConfig(apiBase)
      .then(setConfig)
      .catch((e) => console.error('widget config load failed', e));
  }, [apiBase]);

  // Drive the brand color from the shadow host so the derived tones (gradients,
  // tints, the launcher) all resolve from the configured color, not just the
  // panel. The :host CSS declares --brand-* via color-mix(var(--brand), …).
  useEffect(() => {
    if (!config) return;
    document
      .getElementById('stratus-chat-root')
      ?.style.setProperty('--brand', config.brandColor);
  }, [config]);

  useEffect(() => {
    const storedId = getConversationId();
    if (!storedId) return;
    let cancelled = false;
    getChatHistory(apiBase, storedId).then(async (result) => {
      if (cancelled) return;
      if (result.kind === 'not_found') {
        clearConversationId();
        return;
      }
      if (result.kind === 'error') return;
      const history = result.history;
      if (history.messages.length > 0) {
        setMessages(history.messages.map((m) => ({ role: m.role, content: m.content })));
        setShowDisclaimer(false);
      }
      // Check live state up front. A live session that ended (e.g. closed for
      // visitor inactivity) marks the conversation terminal via its tracking
      // ticket, so the live close states MUST be handled BEFORE the generic
      // terminal branch — otherwise the inactive-close UI (Chat again / ticket
      // number) is unreachable on reload and the visitor just sees a dead end.
      // This mirrors the live-poll transitions in the effect below.
      const live = await getLiveState(apiBase, storedId);
      if (cancelled) return;

      if (live && live.status === 'expired') {
        // No-pickup / agent-disconnect → async fallback (leave a message form),
        // same as the live poll's 'expired' branch.
        setLivePhase('ended');
        setLiveEndedReason(live.endedReason);
        setLiveTicketCode(live.ticketCode);
        setEscalation({ kind: 'form', busy: false });
        return;
      }
      if (live && live.status === 'ended') {
        // Agent- or inactivity-closed → show the close UI with Chat again
        // (+ ticket number / Create a ticket), same as the poll's 'ended' branch.
        setLivePhase('ended');
        setLiveEndedReason(live.endedReason);
        setLiveTicketCode(live.ticketCode);
        setTerminal(true);
        return;
      }
      // Seamless resume: if the conversation has a live session in progress,
      // re-enter live mode where we left off. Seed the cursor to the last
      // hydrated message so the poll only fetches turns that arrived since.
      if (live && (live.status === 'active' || live.status === 'queued')) {
        const last = history.messages[history.messages.length - 1];
        // Verbatim server timestamp (see poll note) — never reformat, or
        // microsecond truncation re-fetches the last message in a loop.
        setLiveCursor(last ? last.createdAt : new Date().toISOString());
        if (live.agentName) setLiveAgentName(live.agentName);
        setLivePhase(live.status === 'active' ? 'active' : 'connecting');
        setShowDisclaimer(false);
        return;
      }

      // No live session (or it's gone): fall back to bot-terminal handling.
      if (history.terminal) {
        setTerminal(true);
        if (history.reason) {
          setEscalation({ kind: 'form', busy: false });
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apiBase]);

  useEffect(() => {
    document.body.appendChild(hiddenTurnstileContainer);
    return () => {
      hiddenTurnstileContainer.remove();
    };
  }, [hiddenTurnstileContainer]);

  // Poll live-agent availability while the panel is open and not yet in live mode.
  useEffect(() => {
    if (view !== 'open' || livePhase !== 'off') return;
    let cancelled = false;
    const tick = () =>
      getLiveAvailability(apiBase).then((a) => {
        if (!cancelled) setLiveAvailable(a);
      });
    tick();
    const id = setInterval(tick, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [apiBase, view, livePhase]);

  // While connecting/active, poll live state. The cursor (last seen createdAt)
  // is seeded the moment live mode begins so prior bot/user history already in
  // `messages` is never re-appended; the poll only returns NEW live turns.
  // Visitor messages are NOT optimistically appended (see handleSend) — they
  // come back through this poll as role:'user' rows after the cursor, which is
  // the single source of truth for live-mode messages (no double-append).
  useEffect(() => {
    if (livePhase !== 'connecting' && livePhase !== 'active') return;
    const conversationId = getConversationId();
    if (!conversationId) return;
    let cancelled = false;
    let cursor = liveCursor;

    const tick = async () => {
      const state = await getLiveState(apiBase, conversationId, cursor);
      if (cancelled || !state) return;

      if (state.messages.length > 0) {
        // Skip the echo of any message we already showed optimistically.
        const pending = pendingEchoRef.current;
        const toAppend: Msg[] = [];
        for (const x of state.messages) {
          if (x.role === 'user' && pending.length > 0 && pending[0] === x.content) {
            pending.shift();
            continue;
          }
          toAppend.push({ role: x.role, content: x.content });
        }
        if (toAppend.length > 0) {
          setMessages((m) => [...m, ...toAppend]);
          // An agent reply landed — chime, and badge the launcher if closed.
          const agentCount = toAppend.filter((x) => x.role === 'assistant').length;
          if (agentCount > 0) {
            playMessageChime();
            if (viewRef.current === 'closed') setUnread((u) => u + agentCount);
          }
        }
        // Use the server timestamp verbatim — do NOT reformat. new Date().toISOString()
        // truncates Postgres microseconds to ms, making the cursor slightly less
        // than the row's real created_at, so `created_at > cursor` re-matches the
        // same message every poll (infinite re-append). The route accepts the raw
        // "+00:00" offset form via z.string().datetime({ offset: true }).
        cursor = state.messages[state.messages.length - 1].createdAt;
        setLiveCursor(cursor);
      }

      if (state.agentName) setLiveAgentName(state.agentName);

      if (state.status === 'active' && livePhase !== 'active') {
        setLivePhase('active');
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: `${state.agentName ?? 'An agent'} joined the chat.` }
        ]);
        playMessageChime();
        if (viewRef.current === 'closed') setUnread((u) => u + 1);
      } else if (state.status === 'expired') {
        setLivePhase('ended');
        setLiveEndedReason(state.endedReason);
        setLiveTicketCode(state.ticketCode);
        setEscalation({ kind: 'form', busy: false }); // no-pickup → async fallback
      } else if (state.status === 'ended') {
        setLivePhase('ended');
        setLiveEndedReason(state.endedReason);
        setLiveTicketCode(state.ticketCode);
        setTerminal(true);
      }
    };

    tick();
    const id = setInterval(tick, 2_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [apiBase, livePhase, liveCursor]);

  // Declared before the `!config` early return — a hook after a conditional
  // return changes the hook order between renders.
  const handleChatAgain = useCallback(() => {
    clearConversationId();
    setMessages([]);
    pendingEchoRef.current = [];
    setLivePhase('off');
    setLiveEndedReason(null);
    setLiveTicketCode(null);
    // Stale name would otherwise label the header of the NEXT live session.
    setLiveAgentName(null);
    setTerminal(false);
    setEscalation({ kind: 'hidden' });
    setHandoff({ kind: 'hidden' });
  }, []);

  if (!config) {
    return view === 'closed' ? <LauncherButton
          unread={unread}
          onClick={() => {
            setUnread(0);
            setView('open');
          }}
        /> : null;
  }

  const handleSend = async (text: string) => {
    // While connecting (queued) OR active, route to the live endpoint instead
    // of Claude. Show the message immediately (optimistic) and record it in the
    // pending-echo queue so the poll doesn't render it a second time.
    if (livePhase === 'connecting' || livePhase === 'active') {
      const conversationId = getConversationId();
      if (!conversationId) return;
      setShowDisclaimer(false);
      setMessages((m) => [...m, { role: 'user', content: text }]);
      pendingEchoRef.current.push(text);
      const sentConversationId = conversationId;
      const ok = await sendLiveMessage(apiBase, conversationId, text);
      if (!ok && getConversationId() === sentConversationId) {
        // Send failed: drop the pending echo and tell the visitor.
        // Guard: skip if the conversation was reset (Chat again) while awaiting.
        const idx = pendingEchoRef.current.lastIndexOf(text);
        if (idx !== -1) pendingEchoRef.current.splice(idx, 1);
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: 'That message did not send. Please try again.' }
        ]);
      }
      return; // do NOT call the bot
    }

    if (busy || terminal) return;
    setShowDisclaimer(false);
    setBusy(true);

    const userMsg: Msg = { role: 'user', content: text };
    setMessages((m) => [...m, userMsg]);

    let conversationId = getConversationId() ?? undefined;
    let turnstileToken: string | undefined;
    if (!conversationId) {
      try {
        turnstileToken = await getTurnstileToken(
          config.turnstileSiteKey,
          hiddenTurnstileContainer
        );
      } catch {
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: 'We could not verify your session. Please refresh the page and try again.'
          }
        ]);
        setBusy(false);
        return;
      }
    }

    let assistantBuf = '';
    setMessages((m) => [...m, { role: 'assistant', content: '' }]);

    try {
      for await (const chunk of postChat(apiBase, {
        conversationId,
        message: text,
        pageUrl: window.location.href,
        turnstileToken
      }) as AsyncGenerator<ChatChunk>) {
        if (chunk.type === 'text' && chunk.text) {
          assistantBuf += chunk.text;
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = { role: 'assistant', content: assistantBuf };
            return next;
          });
        } else if (chunk.type === 'meta' && chunk.meta) {
          if (chunk.meta.conversationId && !conversationId) {
            conversationId = chunk.meta.conversationId;
            setConversationId(conversationId);
          }
          // Tag the streaming turn with its persisted id so the thumbs have
          // something to rate. The meta frame can land before or after the
          // text, so this only stamps the id and leaves content alone.
          if (chunk.meta.messageId) {
            const id = chunk.meta.messageId;
            setMessages((m) => {
              const next = [...m];
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, id };
              }
              return next;
            });
          }
          if (chunk.meta.terminal) {
            setTerminal(true);
            clearConversationId();
          }
          // Both the model's handoff tool (handoff:true) and the keyword
          // pre-flight (escalate:true) lead to the same email-first prompt.
          // Routing to live vs. ticket is decided server-side at /api/handoff
          // once the visitor provides an email (see handleHandoffSubmit).
          if (
            (chunk.meta.handoff || chunk.meta.escalate) &&
            handoff.kind === 'hidden' &&
            livePhase === 'off'
          ) {
            setHandoff({ kind: 'prompt', summary: chunk.meta.handoffSummary, busy: false });
          }
          // A stale bot turn can flip the conversation into live mode. Because
          // an agent may already have sent messages before this stale widget
          // caught up, rebuild the transcript from the server: clear local
          // messages and seed an undefined cursor so the next poll reloads the
          // full live thread without missing earlier agent turns or duplicating.
          if (chunk.meta.reason === 'live_handoff') {
            setMessages([]);
            setLiveCursor(undefined);
            setLivePhase('active');
          }
        } else if (chunk.type === 'error') {
          setMessages((m) => {
            const next = [...m];
            next[next.length - 1] = {
              role: 'assistant',
              content:
                chunk.error === 'rate_limited'
                  ? 'You have sent too many messages. Please wait a moment and try again.'
                  : chunk.error === 'terminal'
                    ? 'This conversation has ended. Please reload to start a new one.'
                    : 'Something went wrong sending your message. Please try again.'
            };
            return next;
          });
          if (chunk.error === 'terminal') {
            setTerminal(true);
            clearConversationId();
          }
        }
      }
    } catch (err) {
      console.error('chat stream error', err);
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: 'assistant',
          content: 'Something went wrong. Please try again.'
        };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  // Visitor provided an email after the bot offered to connect. The server
  // decides live (agent online) vs. ticket (offline) and we reflect that here.
  const handleHandoffSubmit = async (email: string) => {
    const conversationId = getConversationId();
    if (!conversationId) return;
    const summary = handoff.kind === 'prompt' ? handoff.summary : undefined;
    setHandoff({ kind: 'prompt', summary, busy: true });
    const res = await postHandoff(apiBase, { conversationId, email, summary });
    if (res.kind === 'live') {
      setHandoff({ kind: 'hidden' });
      setLiveCursor(new Date().toISOString());
      setLivePhase('connecting');
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content:
            'Thanks — connecting you with our team now. Feel free to add any details while you wait.'
        }
      ]);
    } else if (res.kind === 'ticket') {
      setHandoff({ kind: 'ticket', email, ticketCode: res.ticketCode });
      setTerminal(true);
    } else {
      setHandoff({ kind: 'prompt', summary, busy: false, error: true });
    }
  };

  const handleEscalateSubmit = async (data: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }) => {
    const conversationId = getConversationId();
    if (!conversationId) {
      setEscalation({ kind: 'form', busy: false, error: 'no_conversation' });
      return;
    }
    setEscalation({ kind: 'form', busy: true });
    const result = await postEscalate(apiBase, {
      conversationId,
      email: data.email,
      name: data.name || undefined,
      phone: data.phone || undefined,
      message: data.message || undefined
    });
    if (result.kind === 'ok' || result.kind === 'already_escalated') {
      const ticketCode =
        result.kind === 'ok' || result.kind === 'already_escalated' ? result.ticketCode : undefined;
      setEscalation({ kind: 'done', email: data.email, ticketCode });
      setTerminal(true);
      setLivePhase('ended'); // clear any waiting banner/buttons once ticketed
      return;
    }
    setEscalation({
      kind: 'form',
      busy: false,
      error: result.kind === 'rate_limited' ? 'rate_limited' : 'error'
    });
  };

  return (
    <>
      {view === 'closed' ? (
        <LauncherButton
          unread={unread}
          onClick={() => {
            setUnread(0);
            setView('open');
          }}
        />
      ) : (
        <ChatPanel
          brandColor={config.brandColor}
          disclaimer={config.disclaimer}
          showDisclaimer={showDisclaimer}
          greeting={config.greeting}
          suggestedQuestions={config.suggestedQuestions}
          messages={messages}
          busy={busy}
          terminal={terminal}
          escalation={escalation}
          handoff={handoff}
          liveAvailable={liveAvailable}
          livePhase={livePhase}
          liveAgentName={liveAgentName}
          liveEndedReason={liveEndedReason}
          liveTicketCode={liveTicketCode}
          onLeaveMessage={() => setEscalation({ kind: 'form', busy: false })}
          onClose={() => setView('closed')}
          onSend={handleSend}
          onEscalateSubmit={handleEscalateSubmit}
          onHandoffSubmit={handleHandoffSubmit}
          onChatAgain={handleChatAgain}
          onCreateTicket={() => setEscalation({ kind: 'form', busy: false })}
          onRate={(messageId, rating) => postFeedback(apiBase, { messageId, rating })}
        />
      )}
    </>
  );
}
